import * as Astronomy from 'astronomy-engine';

export interface Location {
    name?: string;
    lat: number;
    lng: number;
    timezone?: string;
}

export interface EclipseRegion {
    name: string;
    type: 'totality' | 'partial';
    maxDuration?: string;
    coverage?: number;
    notableLocations: string[];
    description: string;
}

export interface EclipseEvent {
    id: string;
    name: string;
    shortName: string;
    date: Date;
    type: 'total' | 'partial' | 'annular';
    magnitude: number;

    // Rich content
    description: string;
    significance: string;
    highlights: string[];

    // Regional information
    regions: EclipseRegion[];

    // Educational content
    observingTips: string[];
    scientificImportance: string;
    preparation: string[];

    // Technical details
    maxTotalityDuration?: string;
    pathWidth?: number; // km
    velocityRange?: string; // km/h

    // Visibility calculation function
    visibleFromLocation: (location: Location) => {
        visible: boolean;
        type?: 'total' | 'partial' | 'annular';
        localTime: Date;
        duration?: string;
        coverage?: number;
        peak?: Date;
        magnitude?: number;
    };
}

export interface EclipsePathData {
    name: string;
    date: string;
    type: 'total' | 'partial' | 'annular';
    totalityPath: [number, number][];
    partialZones: {
        center: [number, number];
        radius: number;
        magnitude: number;
        label: string;
    }[];
}

// Enhanced timezone estimation
export const getTimezoneFromCoords = (lat: number, lng: number): string => {
    // Europe
    if (lat >= 35 && lat <= 72) {
        if (lng >= -10 && lng < 5) return 'Europe/London';
        if (lng >= 5 && lng < 15) return 'Europe/Berlin';
        if (lng >= 15 && lng < 25) return 'Europe/Warsaw';
        if (lng >= 25 && lng < 35) return 'Europe/Helsinki';
        if (lng >= 35 && lng < 45) return 'Europe/Moscow';
    }

    // North America
    if (lat >= 25 && lat <= 70) {
        if (lng >= -180 && lng < -150) return 'America/Anchorage';
        if (lng >= -150 && lng < -120) return 'America/Los_Angeles';
        if (lng >= -120 && lng < -105) return 'America/Denver';
        if (lng >= -105 && lng < -90) return 'America/Chicago';
        if (lng >= -90 && lng < -60) return 'America/New_York';
    }

    // Asia
    if (lat >= -10 && lat <= 55) {
        if (lng >= 70 && lng < 82.5) return 'Asia/Kolkata';
        if (lng >= 82.5 && lng < 127.5) return 'Asia/Shanghai';
        if (lng >= 127.5 && lng < 145) return 'Asia/Tokyo';
    }

    // Australia
    if (lat >= -45 && lat <= -10) {
        if (lng >= 110 && lng < 155) return 'Australia/Sydney';
    }

    return 'UTC';
};

// Cache for eclipse calculations to avoid repeated expensive computations
const calculationCache = new Map<string, any>();

// Calculate eclipse visibility at a specific location using astronomy-engine
// Fixed to only calculate for the SPECIFIC eclipse we're showing, not any random eclipse
export const calculateEclipseVisibilityAtLocation = (location: Location, eclipseDate: Date) => {
    const cacheKey = `${location.lat.toFixed(2)}_${location.lng.toFixed(2)}_${eclipseDate.getTime()}`;
    
    // Check cache first
    if (calculationCache.has(cacheKey)) {
        return calculationCache.get(cacheKey);
    }

    console.log(`Calculating eclipse for location: ${location.lat}, ${location.lng} on ${eclipseDate}`);

    try {
        const observer = new Astronomy.Observer(location.lat, location.lng, 0);
        const targetTime = new Astronomy.AstroTime(eclipseDate);
        
        // First, find the global eclipse for our target date to verify it exists
        const globalEclipse = Astronomy.SearchGlobalSolarEclipse(targetTime.AddDays(-1));
        console.log('Global eclipse found:', globalEclipse);
        
        // Check if this global eclipse matches our target date (within 2 days)
        if (!globalEclipse || Math.abs(globalEclipse.peak.date.getTime() - eclipseDate.getTime()) > 2 * 24 * 60 * 60 * 1000) {
            console.log('No matching global eclipse found for target date');
            const result = {
                visible: false,
                localTime: eclipseDate,
                coverage: 0,
                magnitude: 0
            };
            calculationCache.set(cacheKey, result);
            return result;
        }

        // Now search for the local eclipse specifically around the global eclipse time
        const searchStart = globalEclipse.peak.AddDays(-1);
        const localEclipse = Astronomy.SearchLocalSolarEclipse(searchStart, observer);

        console.log('Local eclipse found:', localEclipse);

        let result;
        if (localEclipse && localEclipse.peak) {
            const peakDate = localEclipse.peak.time.date;
            if (!peakDate) {
                console.log('Peak date is undefined');
                result = {
                    visible: false,
                    localTime: eclipseDate,
                    coverage: 0,
                    magnitude: 0
                };
            } else {
                // Only consider this eclipse if it's the SAME eclipse (within 12 hours of global peak)
                const timeDiff = Math.abs(peakDate.getTime() - globalEclipse.peak.date.getTime());
                console.log(`Local peak: ${peakDate}, Global peak: ${globalEclipse.peak.date}, diff: ${timeDiff / (1000 * 60 * 60)} hours`);
                
                if (timeDiff < 12 * 60 * 60 * 1000) { // Within 12 hours means it's the same eclipse
                    const obscuration = localEclipse.obscuration || 0;
                    const coverage = Math.round(obscuration * 100);
                    console.log(`Obscuration: ${obscuration}, Coverage: ${coverage}%`);

                    let eclipseType: 'total' | 'annular' | 'partial' = 'partial';
                    let duration: string | undefined;

                    // Determine eclipse type based on both API kind and obscuration
                    if (localEclipse.kind === Astronomy.EclipseKind.Total || obscuration >= 1.0) {
                        eclipseType = 'total';
                        if (localEclipse.total_begin && localEclipse.total_end) {
                            const beginDate = localEclipse.total_begin.time.date;
                            const endDate = localEclipse.total_end.time.date;
                            if (beginDate && endDate) {
                                const durationMinutes = (endDate.getTime() - beginDate.getTime()) / 60000;
                                const minutes = Math.floor(durationMinutes);
                                const seconds = Math.round((durationMinutes - minutes) * 60);
                                duration = `${minutes}m ${seconds}s`;
                            }
                        }
                    } else if (localEclipse.kind === Astronomy.EclipseKind.Annular) {
                        eclipseType = 'annular';
                        if (localEclipse.total_begin && localEclipse.total_end) {
                            const beginDate = localEclipse.total_begin.time.date;
                            const endDate = localEclipse.total_end.time.date;
                            if (beginDate && endDate) {
                                const durationMinutes = (endDate.getTime() - beginDate.getTime()) / 60000;
                                const minutes = Math.floor(durationMinutes);
                                const seconds = Math.round((durationMinutes - minutes) * 60);
                                duration = `${minutes}m ${seconds}s`;
                            }
                        }
                    } else if (obscuration >= 0.99) {
                        // If obscuration is very high (99%+), it's likely totality even if API doesn't classify it as such
                        eclipseType = 'total';
                        console.log(`High obscuration (${obscuration}) detected, classifying as total eclipse`);
                    }

                    console.log(`Eclipse kind from API: ${localEclipse.kind}, Obscuration: ${obscuration}, Final type: ${eclipseType}`);

                    // An eclipse is visible if it has any coverage at all
                    const isVisible = coverage > 0;
                    console.log(`Eclipse visible: ${isVisible}, type: ${eclipseType}`);

                    result = {
                        visible: isVisible,
                        type: eclipseType,
                        localTime: peakDate || eclipseDate,
                        duration,
                        coverage: coverage,
                        peak: peakDate || eclipseDate,
                        magnitude: obscuration
                    };
                } else {
                    console.log('Local eclipse is not the same as target eclipse (time mismatch)');
                    result = {
                        visible: false,
                        localTime: eclipseDate,
                        coverage: 0,
                        magnitude: 0
                    };
                }
            }
        } else {
            console.log('No local eclipse found');
            result = {
                visible: false,
                localTime: eclipseDate,
                coverage: 0,
                magnitude: 0
            };
        }

        // Cache the result
        calculationCache.set(cacheKey, result);
        console.log('Final result:', result);
        return result;
    } catch (error) {
        console.error('Eclipse calculation error:', error);
        const fallback = {
            visible: false,
            localTime: eclipseDate,
            coverage: 0,
            magnitude: 0
        };
        calculationCache.set(cacheKey, fallback);
        return fallback;
    }
};

// Real upcoming solar eclipses with rich metadata
export const upcomingEclipses: EclipseEvent[] = [
    {
        id: 'total-2026-08-12',
        name: 'Total Solar Eclipse August 2026',
        shortName: 'August 2026 Eclipse',
        date: new Date('2026-08-12T17:47:00Z'),
        type: 'total',
        magnitude: 1.039,
        maxTotalityDuration: '2m 18s',
        pathWidth: 294,
        velocityRange: '1,500-2,100 km/h',

        description: "A spectacular total solar eclipse crossing the North Atlantic, offering exceptional viewing opportunities across Greenland, Iceland, Spain, and western Russia. This eclipse features a relatively wide path of totality and moderate eclipse duration.",

        significance: "This eclipse marks the return of totality to Europe after the 2017 American eclipse captured global attention. It's particularly significant for European observers and will be one of the most accessible total eclipses for the next decade.",

        highlights: [
            "Path crosses major European cities including parts of Spain",
            "Excellent viewing from Iceland with clear skies statistically likely",
            "Up to 2 minutes 18 seconds of totality",
            "Crosses 4 time zones from Greenland to Russia",
            "First total eclipse visible from Spain since 1959"
        ],

        regions: [
            {
                name: "Greenland",
                type: "totality",
                maxDuration: "2m 10s",
                coverage: 100,
                notableLocations: ["Nuuk region", "Southwest coast"],
                description: "Eclipse begins over Greenland's southwest coast, offering unique Arctic viewing conditions."
            },
            {
                name: "Iceland",
                type: "totality",
                maxDuration: "2m 5s",
                coverage: 100,
                notableLocations: ["Reykjavik", "Southwest Peninsula"],
                description: "Passes directly over Reykjavik, making it one of the few capital cities to experience totality."
            },
            {
                name: "Spain",
                type: "totality",
                maxDuration: "1m 55s",
                coverage: 100,
                notableLocations: ["Northern regions", "Cantabria", "León"],
                description: "Northern Spain experiences totality, with major cities having excellent viewing opportunities."
            }
        ],

        observingTips: [
            "Use ISO 12312-2 certified eclipse glasses at all times except during totality",
            "Plan for August weather - bring layers for changing conditions",
            "Iceland offers statistically better weather prospects than other regions",
            "Book accommodations early - eclipse tourism will be heavy"
        ],

        scientificImportance: "This eclipse provides opportunities for corona observation and atmospheric studies, particularly valuable for European research institutions.",

        preparation: [
            "Obtain certified eclipse glasses well in advance",
            "Research local weather patterns for your chosen viewing location",
            "Plan transportation and accommodation early",
            "Download eclipse timing apps for precise local circumstances"
        ],

        visibleFromLocation: (location: Location) => calculateEclipseVisibilityAtLocation(location, new Date('2026-08-12T17:47:00Z'))
    },
    {
        id: 'total-2027-08-02',
        name: 'Total Solar Eclipse August 2027',
        shortName: 'August 2027 Eclipse',
        date: new Date('2027-08-02T10:07:00Z'),
        type: 'total',
        magnitude: 1.079,
        maxTotalityDuration: '6m 23s',
        pathWidth: 258,
        velocityRange: '800-1,200 km/h',

        description: "An extraordinary total solar eclipse with the longest duration of totality in decades - up to 6 minutes and 23 seconds. The path crosses North Africa and the Middle East, offering unparalleled viewing opportunities.",

        significance: "This eclipse offers the longest totality duration of any eclipse until 2150, making it one of the most significant astronomical events of the 21st century.",

        highlights: [
            "Longest totality duration until 2150 - up to 6 minutes 23 seconds",
            "Path crosses the ancient lands of Egypt and the Arabian Peninsula",
            "Excellent desert viewing conditions with minimal cloud cover",
            "Perfect timing for astronomical tourism",
            "Multiple ancient astronomical sites lie within the path"
        ],

        regions: [
            {
                name: "Morocco & Algeria",
                type: "totality",
                maxDuration: "5m 30s",
                coverage: 100,
                notableLocations: ["Atlas Mountains", "Sahara Desert"],
                description: "Desert regions offer excellent viewing with clear skies and minimal light pollution."
            },
            {
                name: "Egypt",
                type: "totality",
                maxDuration: "6m 20s",
                coverage: 100,
                notableLocations: ["Luxor", "Red Sea coast"],
                description: "Near maximum eclipse duration with exceptional viewing conditions."
            },
            {
                name: "Saudi Arabia & Yemen",
                type: "totality",
                maxDuration: "6m 23s",
                coverage: 100,
                notableLocations: ["Arabian Peninsula", "Empty Quarter"],
                description: "Maximum eclipse duration occurs over the Arabian Peninsula."
            }
        ],

        observingTips: [
            "Desert conditions require sun protection and hydration planning",
            "August heat will be intense - early morning viewing recommended",
            "Bring dust protection for equipment",
            "Consider organized eclipse tours for logistics support"
        ],

        scientificImportance: "The extended totality duration makes this eclipse invaluable for corona research, solar physics studies, and atmospheric observations.",

        preparation: [
            "Plan for extreme heat and desert conditions",
            "Secure eclipse tour bookings early - this will be heavily attended",
            "Prepare photography equipment for extended totality",
            "Research local customs and travel requirements"
        ],

        visibleFromLocation: (location: Location) => calculateEclipseVisibilityAtLocation(location, new Date('2027-08-02T10:07:00Z'))
    },
    {
        id: 'total-2028-07-22',
        name: 'Total Solar Eclipse July 2028',
        shortName: 'July 2028 Eclipse',
        date: new Date('2028-07-22T03:17:00Z'),
        type: 'total',
        magnitude: 1.056,
        maxTotalityDuration: '5m 10s',
        pathWidth: 230,
        velocityRange: '1,100-1,600 km/h',

        description: "A magnificent total solar eclipse crossing Australia and New Zealand, providing excellent viewing opportunities with up to 5 minutes and 10 seconds of totality in the Southern Hemisphere's winter.",

        significance: "This eclipse brings totality to major population centers in Australia and New Zealand, making it highly accessible for Southern Hemisphere observers and marking a rare treat for the region.",

        highlights: [
            "Crosses Sydney, Australia - rare totality over a major city",
            "New Zealand experiences totality for the first time since 1965",
            "Winter viewing conditions with crisp, clear skies expected",
            "Up to 5 minutes 10 seconds of totality",
            "Excellent for eclipse photography with stable weather"
        ],

        regions: [
            {
                name: "Australia",
                type: "totality",
                maxDuration: "5m 10s",
                coverage: 100,
                notableLocations: ["Sydney", "Newcastle", "Gold Coast"],
                description: "Major Australian cities experience totality, with Sydney being a primary viewing destination."
            },
            {
                name: "New Zealand",
                type: "totality",
                maxDuration: "4m 18s",
                coverage: 100,
                notableLocations: ["South Island", "Queenstown region"],
                description: "South Island of New Zealand offers spectacular mountain viewing opportunities."
            }
        ],

        observingTips: [
            "July is winter in the Southern Hemisphere - dress warmly",
            "Clear winter skies provide excellent viewing conditions",
            "Sydney harbor will be a prime viewing location",
            "New Zealand's mountains offer unique photographic opportunities"
        ],

        scientificImportance: "This eclipse provides valuable research opportunities for Southern Hemisphere institutions and offers unique atmospheric studies over oceanic regions.",

        preparation: [
            "Book accommodations in Sydney or New Zealand early",
            "Prepare for winter weather conditions",
            "Consider combining with winter tourism activities",
            "Plan for heavy eclipse tourist traffic in major cities"
        ],

        visibleFromLocation: (location: Location) => calculateEclipseVisibilityAtLocation(location, new Date('2028-07-22T03:17:00Z'))
    }
];

// Generate eclipse path data using astronomy-engine (simplified for performance)
export const generateEclipsePathData = (): EclipsePathData[] => {
    const eclipses: EclipsePathData[] = [];

    try {
        let searchTime = new Astronomy.AstroTime(new Date());

        for (let i = 0; i < 3; i++) {
            const globalEclipse = Astronomy.SearchGlobalSolarEclipse(searchTime);

            if (globalEclipse) {
                const eclipseData: EclipsePathData = {
                    name: `${globalEclipse.kind === Astronomy.EclipseKind.Total ? 'Total' :
                        globalEclipse.kind === Astronomy.EclipseKind.Annular ? 'Annular' : 'Partial'} Solar Eclipse ${globalEclipse.peak.date.getFullYear()}`,
                    date: globalEclipse.peak.date.toDateString(),
                    type: globalEclipse.kind === Astronomy.EclipseKind.Total ? 'total' :
                        globalEclipse.kind === Astronomy.EclipseKind.Annular ? 'annular' : 'partial',
                    totalityPath: [],
                    partialZones: []
                };

                const centerLat = globalEclipse.latitude;
                const centerLng = globalEclipse.longitude;

                if (globalEclipse.kind === Astronomy.EclipseKind.Total || globalEclipse.kind === Astronomy.EclipseKind.Annular) {
                    const pathPoints: [number, number][] = [];
                    for (let i = -5; i <= 5; i++) {
                        pathPoints.push([centerLat, centerLng + i * 2]);
                    }
                    eclipseData.totalityPath = pathPoints;
                }

                const earthRadius = 6371000;
                const zones = [
                    { radius: Math.round(earthRadius * 0.03), magnitude: 1.0, label: 'Totality (Approximate)' },
                    { radius: Math.round(earthRadius * 0.08), magnitude: 0.8, label: '~80% Partial' },
                    { radius: Math.round(earthRadius * 0.16), magnitude: 0.6, label: '~60% Partial' },
                    { radius: Math.round(earthRadius * 0.24), magnitude: 0.4, label: '~40% Partial' },
                    { radius: Math.round(earthRadius * 0.31), magnitude: 0.2, label: '~20% Partial' }
                ];

                eclipseData.partialZones = zones.map(zone => ({
                    center: [centerLng, centerLat] as [number, number],
                    radius: zone.radius,
                    magnitude: zone.magnitude,
                    label: zone.label
                }));

                eclipses.push(eclipseData);

                const nextEclipse = Astronomy.NextGlobalSolarEclipse(globalEclipse.peak);
                if (nextEclipse) {
                    searchTime = nextEclipse.peak;
                } else {
                    break;
                }
            }
        }
    } catch (error) {
        console.error('Failed to generate eclipse paths:', error);
    }

    return eclipses;
};
